import React, { Fragment, useEffect, useState } from "react";
import { Dialog, Transition } from "@headlessui/react";
import toast from "react-hot-toast";
import { useCreateAdmin } from "utils/api/admin.api";
import { type CreateAdminRequest } from "utils/types/user.type";

interface CreateAdminProps {
    isOpen: boolean;
    setIsOpen: (open: boolean) => void;
}

export default function CreateAdmin({ isOpen, setIsOpen }: CreateAdminProps) {
    const { mutate: createAdmin, isLoading, isSuccess, isError, error } =
        useCreateAdmin();

    const [formData, setFormData] = useState<CreateAdminRequest>({
        email: "",
        password: "",
        name: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prevState) => ({
            ...prevState,
            [name]: value,
        }));
    };

    const handleSubmit = () => {
        if (!formData.email || !formData.password) {
            toast.error("Email and password are required");
            return;
        }

        if (formData.password.length < 8) {
            toast.error("Password must be at least 8 characters");
            return;
        }

        createAdmin(formData);
    };

    useEffect(() => {
        if (isSuccess) {
            toast.success("Admin created successfully!");
            setFormData({
                email: "",
                password: "",
                name: "",
            });
            setIsOpen(false);
        }
    }, [isSuccess, setIsOpen]);

    useEffect(() => {
        if (isError && error) {
            toast.error(
                error?.message || "Failed to create admin. Please try again."
            );
        }
    }, [isError, error]);

    const handleClose = () => {
        setFormData({
            email: "",
            password: "",
            name: "",
        });
        setIsOpen(false);
    };

    return (
        <Transition.Root show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-[9999]" onClose={handleClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity z-[9999]" />
                </Transition.Child>

                <div className="fixed inset-0 z-[9999] w-screen overflow-y-auto">
                    <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                            enterTo="opacity-100 translate-y-0 sm:scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                            leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                        >
                            <Dialog.Panel className="relative z-[10000] transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-md">
                                <div className="bg-white px-4 pb-4 pt-5 sm:p-4 sm:pb-4">
                                    <div className="">
                                        <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                                            <Dialog.Title
                                                as="h3"
                                                className="text-base font-semibold leading-6 text-gray-900"
                                            >
                                                Register New Admin
                                            </Dialog.Title>
                                            <div className="mt-4">
                                                <div className="mt-2">
                                                    <label
                                                        htmlFor="name"
                                                        className="block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        Name (Optional)
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="name"
                                                            name="name"
                                                            type="text"
                                                            value={formData.name}
                                                            onChange={handleChange}
                                                            placeholder="Enter admin name"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-0 ps-2 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>

                                                    <label
                                                        htmlFor="email"
                                                        className="mt-2 block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        Email <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="email"
                                                            name="email"
                                                            type="email"
                                                            value={formData.email}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Enter admin email"
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-0 ps-2 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>

                                                    <label
                                                        htmlFor="password"
                                                        className="mt-2 block text-sm font-medium leading-6 text-gray-900"
                                                    >
                                                        Password <span className="text-red-500">*</span>
                                                    </label>
                                                    <div className="mt-2">
                                                        <input
                                                            id="password"
                                                            name="password"
                                                            type="password"
                                                            value={formData.password}
                                                            onChange={handleChange}
                                                            required
                                                            placeholder="Enter password (min 8 characters)"
                                                            minLength={8}
                                                            className="block w-full rounded-md border-0 py-1.5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:outline-0 ps-2 placeholder:text-gray-400 sm:text-sm sm:leading-6"
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                                    <button
                                        type="button"
                                        disabled={isLoading}
                                        className="inline-flex w-full justify-center rounded-md bg-cyan-300 px-3 py-2 text-sm font-semibold text-black shadow-sm hover:bg-cyan-800 sm:ml-3 sm:w-auto ring-1 ring-inset ring-cyan-800 disabled:opacity-50"
                                        onClick={handleSubmit}
                                    >
                                        {isLoading ? (
                                            <div className="w-5 h-5 rounded-full animate-spin mx-auto border-2 border-solid border-black-800 border-t-transparent"></div>
                                        ) : (
                                            "Register"
                                        )}
                                    </button>
                                    <button
                                        type="button"
                                        className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                                        onClick={handleClose}
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition.Root>
    );
}

